import { useEffect, useRef, useState } from 'react';

export default function WhoSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const audiences = [
    {
      icon: 'ri-building-4-line',
      title: 'Family Office Principals & Next Gen Heirs',
      description: 'Stewards of multi-generational wealth navigating governance and legacy.'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Private Equity, VC & Real Assets Investors',
      description: 'Capital allocators shaping the future of private markets.'
    },
    {
      icon: 'ri-rocket-line',
      title: 'Founders & CEOs',
      description: 'Leaders building high-growth companies with global impact.'
    },
    {
      icon: 'ri-palette-line',
      title: 'Stewards of Luxury, Art & Culture',
      description: 'Curators of lifestyle, philanthropy, and cultural influence.'
    },
    {
      icon: 'ri-compass-line',
      title: 'Advisors & Strategists',
      description: 'Architects of cross-border wealth and sophisticated structures.'
    },
    {
      icon: 'ri-global-line',
      title: 'Global Ecosystem Builders',
      description: 'Connectors bridging capital, culture, and opportunity.'
    }
  ];

  return (
    <section ref={sectionRef} className="relative py-32 bg-navy-950">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-950 to-navy-950"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="text-champagne-400 text-sm font-sans font-medium tracking-[0.2em] uppercase">
            The Room
          </span>
          
          <h2 className="font-display text-5xl lg:text-6xl font-bold text-white mt-6 mb-8 leading-[1.1]">
            Who This Summit<br />Is For
          </h2>
          
          <div className="w-16 h-0.5 bg-champagne-400 mx-auto"></div>
        </div>

        {/* Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {audiences.map((audience, index) => (
            <div
              key={index}
              className={`group relative bg-navy-900/50 backdrop-blur-sm border border-navy-800 p-8 hover:bg-navy-800/50 hover:border-champagne-500/50 transition-all duration-500 cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Icon */}
              <div className="w-14 h-14 flex items-center justify-center bg-champagne-500/10 rounded-full mb-6 group-hover:bg-champagne-500/20 transition-colors duration-300">
                <i className={`${audience.icon} text-3xl text-champagne-400`}></i>
              </div>

              {/* Content */}
              <h3 className="font-display text-2xl font-semibold text-white mb-4 leading-tight">
                {audience.title}
              </h3>
              
              <p className="text-navy-300 font-sans leading-relaxed">
                {audience.description}
              </p>

              {/* Hover Accent */}
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-champagne-500 to-champagne-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
            </div>
          ))}
        </div>

        {/* Bottom Text */}
        <div className={`text-center mt-16 transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="text-lg text-navy-300 font-sans italic">
            A curated gathering of women who shape the future of global capital
          </p>
        </div>
      </div>
    </section>
  );
}